import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Chat from "@/pages/Chat";

const Admin = lazy(() => import("@/pages/Admin"));
const Today = lazy(() => import("@/pages/Today"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const Memory = lazy(() => import("@/pages/Memory"));
const Files = lazy(() => import("@/pages/Files"));
const Approvals = lazy(() => import("@/pages/Approvals"));
const Integrations = lazy(() => import("@/pages/Integrations"));
const ConnectedAccounts = lazy(() => import("@/pages/ConnectedAccounts"));
const MailSearch = lazy(() => import("@/pages/MailSearch"));
const Goals = lazy(() => import("@/pages/Goals"));
const DecisionBoard = lazy(() => import("@/pages/DecisionBoard"));
const Missions = lazy(() => import("@/pages/Missions"));
const MissionDetail = lazy(() => import("@/pages/MissionDetail"));

function PageLoading({ label = "Loading..." }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF9F5] text-slate-500 text-sm">
      {label}
    </div>
  );
}

function App() {
  return (
    <div className="App min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot" element={<ForgotPassword />} />
            <Route path="/reset" element={<ResetPassword />} />
            <Route path="/" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
            <Route path="/c/:chatId" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
            <Route
              path="/today"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoading label="Loading today..." />}><Today /></Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoading label="Loading tasks..." />}><Tasks /></Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/memory"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoading label="Loading memory..." />}><Memory /></Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/files"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoading label="Loading files..." />}><Files /></Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/approvals"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoading label="Loading approvals..." />}><Approvals /></Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrations"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoading label="Loading integrations..." />}><Integrations /></Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/accounts"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoading label="Loading accounts..." />}><ConnectedAccounts /></Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/mail"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoading label="Loading mail search..." />}><MailSearch /></Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/goals"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoading label="Loading goals..." />}><Goals /></Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/decisions"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoading label="Loading decisions..." />}><DecisionBoard /></Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/missions"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoading label="Loading missions..." />}><Missions /></Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/missions/:missionId"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoading label="Loading mission..." />}><MissionDetail /></Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly>
                  <Suspense fallback={<PageLoading label="Loading admin..." />}><Admin /></Suspense>
                </ProtectedRoute>
              }
            />
          </Routes>
          <Toaster position="top-center" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
